import {create} from "@/store/createStore";
import type {TCompanyProfile} from "@/store/types";
import {withStreetNumberCheck} from "@/utils/addressUtils";
import isEqual from "lodash/isEqual";
import uniq from "lodash/uniq";
import {z, ZodSafeParseResult} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

const AddressFieldsSchema = z.object({
  address_city: z
    .string({error: "company_profile:missing_address_city_error"})
    .min(1, "company_profile:missing_address_city_error"),
  address_postal: z
    .string({error: "company_profile:missing_address_postal_error"})
    .min(1, "company_profile:missing_address_postal_error"),
  address_street: z
    .string({error: "company_profile:missing_address_street_error"})
    .min(1, "company_profile:missing_address_street_error"),
  country: z.string().nullish(),
});

export const AddressSchema = withStreetNumberCheck({
  countryKey: "country",
  schema: AddressFieldsSchema,
  streetKey: "address_street",
});

const CompanyBasicSchema = z.object({
  company_name: z.string().min(1, "company_profile:missing_company_name_error"),
  website: z.string().min(1, "company_profile:missing_website_error"),
});

const PhoneNumberSchema = z.object({
  phonenumber: z.string().min(1, "company_profile:missing_phonenumber_error"),
});

const CompanySchema = withStreetNumberCheck({
  countryKey: "country",
  schema: z.object({
    ...AddressFieldsSchema.shape,
    ...CompanyBasicSchema.shape,
    ...PhoneNumberSchema.shape,
  }),
  streetKey: "address_street",
});

export const getMissingProfileFields = (
  profile: Partial<Record<keyof z.input<typeof CompanySchema>, string | null>>,
): string[] => {
  const result = CompanySchema.safeParse(profile);

  return result.success ? [] : uniq(result.error.issues.map(issue => issue.message));
};

type TCompanyProfileState = TCompanyProfile & {
  isPhoneNumberLengthCorrect: boolean;
  readonlyUsername: string;
  readonlyTikTokUsername: string | null;
};

// Admins will still use `company.user.id` for updating package subscriptions
type TState = {
  company: TCompanyProfileState;
  errors: Record<string, string>;
  savedCompany: TCompanyProfileState;
  addressValidation(): boolean;
  applyValidationErrors<T>(result: ZodSafeParseResult<T>): boolean;
  companyFormValidation(): boolean;
  discardChanges(): void;
  hasUnsavedChanges(): boolean;
  personalInfoValidation(): boolean;
  phoneNumberValidation(): boolean;
  reset(): void;
  resetErrors(): void;
  setCompany(data: TCompanyProfile): void;
  setError<K extends keyof NonNullable<TState["company"]>>(key: K, value: string): void;
  setValue<K extends keyof NonNullable<TState["company"]>>(key: K, value: NonNullable<TState["company"]>[K]): void;
};

const companyInitialState = {} as TCompanyProfileState;

const createCompanyStore = (name: string) =>
  create<TState>()(
    immer(
      devtools(
        (set, get) => ({
          company: companyInitialState,
          errors: {},
          savedCompany: companyInitialState,
          addressValidation: () => {
            set(state => {
              delete state.errors.address_city;
              delete state.errors.address_postal;
              delete state.errors.address_street;
            });

            return get().applyValidationErrors(AddressSchema.safeParse(get().company));
          },
          applyValidationErrors: <T>(result: ZodSafeParseResult<T>): boolean => {
            if (!result.success) {
              result.error.issues.map(issue =>
                set(state => {
                  if (state.errors) {
                    state.errors[issue.path[0].toString()] = issue.message;
                  } else {
                    state.errors = {[issue.path[0]]: issue.message};
                  }
                }),
              );
            }

            return result.success;
          },
          companyFormValidation: () => get().applyValidationErrors(CompanySchema.safeParse(get().company)),
          discardChanges: () =>
            set(
              state => {
                state.company = state.savedCompany;
                state.errors = {};
              },
              false,
              `${name}/discardChanges`,
            ),
          hasUnsavedChanges: () => !isEqual(get().company, get().savedCompany),
          personalInfoValidation: () => get().applyValidationErrors(CompanyBasicSchema.safeParse(get().company)),
          phoneNumberValidation: () => get().applyValidationErrors(PhoneNumberSchema.safeParse(get().company)),
          reset: () =>
            set(
              state => {
                state.company = companyInitialState;
                state.errors = {};
                state.savedCompany = companyInitialState;
              },
              false,
              `${name}/reset`,
            ),
          resetErrors: () =>
            set(
              state => {
                state.errors = {};
              },
              false,
              `${name}/resetErrors`,
            ),
          setCompany: data => {
            const phoneNumber = data.phonenumber?.replaceAll(" ", "");

            return set(
              state => {
                if (state.company.id !== data.id) state.errors = {};

                state.company = {
                  ...data,
                  phonenumber: phoneNumber ?? "",
                  isPhoneNumberLengthCorrect: true,
                  readonlyUsername: data.username!,
                  readonlyTikTokUsername: data.tiktok_username!,
                };

                if (!data.package_subscription?.package_type) {
                  state.company.package_subscription = null;
                }

                state.savedCompany = state.company;
              },
              false,
              `${name}/setCompany`,
            );
          },
          setError: (key, value) =>
            set(
              state => {
                state.errors[key] = value;
              },
              false,
              `${name}/setError`,
            ),
          setValue: (key, value) => {
            set(
              state => {
                state.company[key] = value;
              },
              false,
              `${name}/setValue`,
            );

            const errorKey = key.split(".").pop()!;

            if (!!get().errors && errorKey in get().errors! && !!get().errors![errorKey].length) {
              set(state => {
                delete state.errors![errorKey];
              });
            }
          },
        }),
        {name},
      ),
    ),
  );

export const useCompanyStore = createCompanyStore("companyStore");
export const useSettingsCompanyStore = createCompanyStore("settingsCompanyStore");
